import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Share2,
  Building2,
  UserCheck,
  Users,
  ShieldCheck,
  BookOpen,
  Search,
  LayoutGrid,
  List,
  Globe,
  MapPin,
  Sparkles,
  ArrowLeft,
  CheckCircle2,
  ExternalLink,
  Copy,
  Check,
  Calendar,
  Layers,
  Award,
  ChevronRight,
  Eye,
  Clock,
  SlidersHorizontal,
  RotateCcw,
  ShoppingCart,
} from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import type { Book } from '../../../shared/types/index.js';
import { LazyBookCover, LazyAvatar, SeoHead, createProfilePageSchema, BreadcrumbNav, PaginationNav } from '../../../shared/components/index.js';
import { slugify } from '../../../shared/utils/stringUtils.js';
import { ShareShowcaseModal } from './ShareShowcaseModal.js';
import { useNotificationStore } from '../../notifications/index.js';
import { useModalStore } from '../../../shared/store/useModalStore.js';
import { CustomSelect } from '../../books/components/book-form/CustomSelect.js';

interface PublicShowcaseViewProps {
  type: 'penulis' | 'penerbit' | 'organisasi';
  identifier: string;
  onBackToDirectory?: () => void;
  onSelectBook?: (book: Book) => void;
}

export const PublicShowcaseView: React.FC<PublicShowcaseViewProps> = ({
  type,
  identifier,
  onBackToDirectory,
  onSelectBook,
}) => {
  const [searchQuery, setSearchQuery] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('q') || '';
  });
  const [selectedCategory, setSelectedCategory] = useState(() => {
    if (typeof window === 'undefined') return 'semua';
    return new URLSearchParams(window.location.search).get('genre') || 'semua';
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    if (typeof window === 'undefined') return 'semua';
    return new URLSearchParams(window.location.search).get('tahun') || 'semua';
  });
  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window === 'undefined') return false;
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.has('genre') || searchParams.has('tahun');
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [copiedHeaderLink, setCopiedHeaderLink] = useState(false);

  // Sync state changes with URL query parameters for shareability
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);

    if (searchQuery.trim()) {
      url.searchParams.set('q', searchQuery);
    } else {
      url.searchParams.delete('q');
    }

    if (selectedCategory && selectedCategory !== 'semua') {
      url.searchParams.set('genre', selectedCategory);
    } else {
      url.searchParams.delete('genre');
    }

    if (selectedYear && selectedYear !== 'semua') {
      url.searchParams.set('tahun', selectedYear);
    } else {
      url.searchParams.delete('tahun');
    }

    window.history.replaceState({}, '', url.pathname + url.search);
  }, [searchQuery, selectedCategory, selectedYear]);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };

  const { showToast } = useNotificationStore();
  const { setViewingBook } = useModalStore();

  const handleBookClick = (book: Book) => {
    if (onSelectBook) {
      onSelectBook(book);
    } else {
      setViewingBook(book);
    }
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ['showcase', type, identifier],
    queryFn: () => api.getShowcase(type, identifier),
    staleTime: 1000 * 60 * 5, // 5 minutes cache
  });

  const profile = data?.profile;
  const books = data?.books || [];
  const stats = data?.stats;
  const categories = data?.categories || [];

  const profileSlug = useMemo(() => {
    return profile?.name ? slugify(profile.name) : slugify(identifier);
  }, [profile?.name, identifier]);

  const fullShareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/${type === 'penerbit' ? 'penerbit' : type === 'organisasi' ? 'organisasi' : 'penulis'}/${profileSlug}`
    : '';

  // Extract unique publication years for filter option
  const uniqueYears = useMemo(() => {
    const years = books.map((b) => b.publicationYear).filter(Boolean);
    return Array.from(new Set(years)).sort((a, b) => b - a);
  }, [books]);

  const profileStructuredData = useMemo(() => {
    if (!profile) return undefined;
    return createProfilePageSchema({
      name: profile.name,
      role: type,
      organization: profile.organization || undefined,
      bio: profile.bio || undefined,
      avatarUrl: profile.avatarUrl || profile.profileImageUrl || undefined,
      url: fullShareUrl,
      verifiedBooksCount: books.length,
    });
  }, [profile, type, fullShareUrl, books.length]);

  // Filter books inside showcase by search query, category, and publication year
  const filteredBooks = useMemo(() => {
    return books.filter((b) => {
      const matchesSearch =
        !searchQuery.trim() ||
        b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.uibn.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (b.synopsis && b.synopsis.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory =
        selectedCategory === 'semua' || b.category.toLowerCase() === selectedCategory.toLowerCase();

      const matchesYear =
        selectedYear === 'semua' || String(b.publicationYear) === selectedYear;

      return matchesSearch && matchesCategory && matchesYear;
    });
  }, [books, searchQuery, selectedCategory, selectedYear]);

  const categoryOptions = useMemo(() => {
    return [
      { value: 'semua', label: `Semua Kategori (${books.length})` },
      ...categories.map((c) => ({
        value: c.category.toLowerCase(),
        label: `${c.category} (${c.count})`,
      })),
    ];
  }, [categories, books.length]);

  const yearOptions = useMemo(() => {
    return [
      { value: 'semua', label: `Semua Tahun (${books.length} buku)` },
      ...uniqueYears.map((year) => ({
        value: String(year),
        label: `Tahun ${year}`,
      })),
    ];
  }, [uniqueYears, books.length]);

  const totalBookPages = Math.max(1, Math.ceil(filteredBooks.length / itemsPerPage));

  const paginatedBooks = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredBooks.slice(start, start + itemsPerPage);
  }, [filteredBooks, currentPage, itemsPerPage]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(decodeURIComponent(fullShareUrl));
      setCopiedHeaderLink(true);
      showToast('Tautan Disalin', 'Tautan halaman publik resmi telah disalin ke papan klip', 'success');
      setTimeout(() => setCopiedHeaderLink(false), 2500);
    } catch {
      // Fallback
    }
  };

  if (isLoading) {
    return (
      <div className="py-16 flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center animate-spin">
          <BookOpen className="w-6 h-6 text-indigo-600" />
        </div>
        <p className="text-xs font-semibold text-slate-500">Memuat Katalog Publik UIBN...</p>
      </div>
    );
  }

  if (isError || !data || !profile) {
    return (
      <div className="py-16 text-center max-w-md mx-auto space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto border border-rose-100 shadow-xs">
          <Building2 className="w-8 h-8" />
        </div>
        <div>
          <h3 className="font-bold text-slate-900 text-lg">Halaman Publik Tidak Ditemukan</h3>
          <p className="text-xs text-slate-500 mt-1">
            Data {type === 'organisasi' ? 'organisasi/komunitas' : type === 'penerbit' ? 'penerbit' : 'penulis'} "{identifier}" belum terdaftar atau belum memiliki buku terbit berkode UIBN.
          </p>
        </div>
        {onBackToDirectory && (
          <button
            type="button"
            onClick={onBackToDirectory}
            className="px-4 py-2 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition cursor-pointer"
          >
            Lihat Direktori Publik
          </button>
        )}
      </div>
    );
  }

  const typeLabel = type === 'organisasi' ? 'Organisasi & Komunitas' : type === 'penerbit' ? 'Penerbit' : 'Penulis';

  return (
    <div className="space-y-8 pb-12">
      <SeoHead
        title={`Katalog Buku ${profile.name} (${typeLabel})`}
        description={profile.bio || `Lihat seluruh katalog naskah buku ber-UIBN resmi, e-sertifikat, dan portofolio literasi terbit dari ${profile.name} di Platform UIBN.`}
        ogType="profile"
        ogImage={profile.avatarUrl || profile.profileImageUrl || books[0]?.coverUrl || undefined}
        keywords={[profile.name, type, 'katalog buku', 'UIBN profil', 'buku terbit']}
        structuredData={profileStructuredData}
      />

      {/* Top Navigation & Breadcrumb */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {onBackToDirectory ? (
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={onBackToDirectory}
              className="flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-indigo-600 bg-white border border-slate-200/80 px-3.5 py-2 rounded-xl transition shadow-2xs hover:border-indigo-200 cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4 text-indigo-600" />
              <span>Kembali ke Direktori</span>
            </button>
            <BreadcrumbNav
              items={[
                { name: 'Beranda', url: '/' },
                { name: 'Direktori', url: '/direktori' },
                { name: typeLabel, url: `/direktori?tab=${type}` },
                { name: profile.name, url: `/${type}/${slugify(profile.name || identifier)}` },
              ]}
              className="hidden sm:flex"
            />
          </div>
        ) : (
          <BreadcrumbNav
            items={[
              { name: 'Beranda', url: '/' },
              { name: 'Direktori', url: '/direktori' },
              { name: typeLabel, url: `/direktori?tab=${type}` },
              { name: profile.name, url: `/${type}/${slugify(profile.name || identifier)}` },
            ]}
          />
        )}

        <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
          <button
            type="button"
            onClick={handleCopyLink}
            className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:text-indigo-600 hover:border-indigo-200 transition shadow-2xs cursor-pointer min-w-0 text-center"
          >
            {copiedHeaderLink ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                <span className="text-emerald-600 truncate">Tersalin!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                <span className="truncate">Salin Tautan</span>
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => setIsShareModalOpen(true)}
            className="flex-1 sm:flex-initial flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition shadow-xs cursor-pointer min-w-0 text-center"
          >
            <Share2 className="w-4 h-4 text-indigo-300 shrink-0" />
            <span className="truncate">Bagikan Katalog</span>
          </button>
        </div>
      </div>

      {/* Hero Header Card */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-800">
        <div className="absolute inset-0 bg-[radial-gradient(#6366f1_1px,transparent_1px)] [background-size:24px_24px] opacity-15 pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4 sm:gap-6 min-w-0">
            <LazyAvatar
              src={profile.profileImageUrl || profile.avatarUrl || undefined}
              alt={profile.name}
              size="2xl"
              priority
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-indigo-600/30 border-2 border-indigo-400/40 backdrop-blur-md shrink-0 shadow-lg"
              fallbackIcon={
                type === 'organisasi' ? (
                  <Users className="w-9 h-9 sm:w-11 sm:h-11 text-amber-300" />
                ) : type === 'penerbit' ? (
                  <Building2 className="w-9 h-9 sm:w-11 sm:h-11 text-indigo-300" />
                ) : (
                  <UserCheck className="w-9 h-9 sm:w-11 sm:h-11 text-indigo-300" />
                )
              }
            />

            <div className="space-y-2 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${
                  type === 'organisasi' 
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-400/30' 
                    : 'bg-indigo-500/20 text-indigo-300 border border-indigo-400/30'
                }`}>
                  {type === 'organisasi' ? 'Organisasi & Komunitas' : type === 'penerbit' ? 'Penerbit Resmi' : 'Nama Penulis'}
                </span>

                {profile.isPublisherVerified && (
                  <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Terverifikasi UIBN</span>
                  </span>
                )}

                {profile.publisherCode && (
                  <span className="px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold bg-slate-800 text-slate-300 border border-slate-700">
                    Kode: {profile.publisherCode}
                  </span>
                )}
              </div>

              <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight leading-snug">
                {profile.name}
              </h1>

              {profile.publisherLegalName && profile.publisherLegalName !== profile.name && (
                <p className="text-xs text-indigo-200/80 font-medium">
                  Nama Badan Hukum: <span className="text-white">{profile.publisherLegalName}</span>
                </p>
              )}

              <div className="flex items-center gap-4 flex-wrap text-xs text-slate-300 pt-1">
                {profile.publisherWebsite && (
                  <a
                    href={
                      profile.publisherWebsite.startsWith('http')
                        ? profile.publisherWebsite
                        : `https://${profile.publisherWebsite}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 hover:text-indigo-300 transition underline underline-offset-2"
                  >
                    <Globe className="w-3.5 h-3.5 text-indigo-400" />
                    <span>{profile.publisherWebsite.replace(/^https?:\/\//, '')}</span>
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                )}

                {profile.publisherAddress && (
                  <span className="flex items-start gap-1.5 text-slate-300">
                    <MapPin className="w-3.5 h-3.5 text-indigo-400 mt-0.5 shrink-0" />
                    <span className="break-words line-clamp-2 md:line-clamp-none max-w-xs sm:max-w-md leading-relaxed">
                      {profile.publisherAddress}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full md:w-auto shrink-0 pt-4 md:pt-0 border-t md:border-t-0 border-slate-800">
            <div className="p-3.5 rounded-2xl bg-slate-800/80 border border-slate-700/60 text-center">
              <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                Buku Terbit
              </span>
              <span className="text-xl font-extrabold text-white mt-0.5 block">{stats?.totalBooks || books.length}</span>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-800/80 border border-slate-700/60 text-center">
              <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                Kategori
              </span>
              <span className="text-xl font-extrabold text-indigo-300 mt-0.5 block">
                {stats?.categoryCount || categories.length}
              </span>
            </div>

            <div className="p-3.5 rounded-2xl bg-slate-800/80 border border-slate-700/60 text-center col-span-2 sm:col-span-1">
              <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">
                Tahun Terbit
              </span>
              <span className="text-sm font-bold text-slate-200 mt-1 block font-mono">
                {stats?.yearRange || '-'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Books Information Banner */}
      {Boolean(stats?.pendingBooksCount && stats.pendingBooksCount > 0) && (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200/90 text-amber-900 flex items-start gap-3 shadow-2xs">
          <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="space-y-0.5 text-xs">
            <h4 className="font-bold text-amber-950 text-sm">
              Status Pengajuan Naskah ({stats?.pendingBooksCount} Naskah Menunggu Verifikasi)
            </h4>
            <p className="text-amber-800/90 leading-relaxed">
              Terdapat <strong>{stats?.pendingBooksCount} naskah buku</strong> yang saat ini dalam tahap pengajuan atau peninjauan oleh admin UIBN. Naskah UIBN akan secara otomatis ditampilkan di katalog publik ini segera setelah status verifikasi berubah menjadi <strong>Disetujui</strong>.
            </p>
          </div>
        </div>
      )}

      {/* Catalog Search & Filtering Section */}
      <div className="bg-white rounded-3xl border border-slate-200 p-5 sm:p-6 shadow-2xs space-y-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" />
              <span>Katalog Buku Berkode UIBN</span>
            </h2>
            <p className="text-xs text-slate-500">
              Menampilkan {filteredBooks.length} dari {books.length} karya resmi terdaftar
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
            {/* Search Input */}
            <div className="relative flex-1 sm:w-64">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Cari judul, sinopsis, atau UIBN..."
                className="w-full pl-9 pr-3.5 py-2.5 text-xs border border-slate-200 rounded-xl bg-slate-50/50 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-600 transition"
              />
            </div>

            {/* Collapsible Filter Toggle Button */}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center gap-1.5 px-3.5 py-2.5 text-xs font-bold rounded-xl border transition cursor-pointer ${
                showFilters || selectedCategory !== 'semua' || selectedYear !== 'semua'
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-600 shadow-3xs'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>Filter Lanjutan</span>
              {(selectedCategory !== 'semua' || selectedYear !== 'semua') && (
                <span className="w-2 h-2 rounded-full bg-indigo-600"></span>
              )}
            </button>

            {/* View Mode Toggle */}
            <div className="flex items-center justify-center bg-slate-100 p-1 rounded-xl border border-slate-200/80 shrink-0 self-center sm:self-auto">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-lg transition cursor-pointer ${
                  viewMode === 'grid' ? 'bg-white text-indigo-600 shadow-3xs font-bold' : 'text-slate-400 hover:text-slate-600'
                }`}
                title="Tampilan Grid"
              >
                <LayoutGrid className="w-4 h-4" />
              </button>

              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-lg transition cursor-pointer ${
                  viewMode === 'table' ? 'bg-white text-indigo-600 shadow-3xs font-bold' : 'text-slate-400 hover:text-slate-600'
                }`}
                title="Tampilan Daftar"
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Category Pill Filters (Quick Navigation) */}
        {categories.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => handleCategoryChange('semua')}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition cursor-pointer ${
                selectedCategory === 'semua'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              Semua Kategori ({books.length})
            </button>

            {categories.map((c) => (
              <button
                key={c.category}
                type="button"
                onClick={() => handleCategoryChange(c.category.toLowerCase())}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition cursor-pointer ${
                  selectedCategory.toLowerCase() === c.category.toLowerCase()
                    ? 'bg-indigo-600 text-white shadow-xs'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {c.category} ({c.count})
              </button>
            ))}
          </div>
        )}

        {/* Collapsible Advanced Filters Panel */}
        {showFilters && (
          <div className="p-4 bg-slate-50/80 border border-slate-200/60 rounded-2xl mt-2 space-y-4 animate-in slide-in-from-top-2 duration-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-end">
              {/* Genre / Kategori Dropdown */}
              <CustomSelect
                label="Genre / Kategori Buku"
                value={selectedCategory}
                onChange={(val) => handleCategoryChange(val)}
                options={categoryOptions}
                className="w-full"
              />

              {/* Tahun Terbit Dropdown */}
              <CustomSelect
                label="Tahun Penerbitan"
                value={selectedYear}
                onChange={(val) => {
                  setSelectedYear(val);
                  setCurrentPage(1);
                }}
                options={yearOptions}
                className="w-full"
              />

              {/* Action Buttons */}
              <div className="flex items-center justify-between sm:justify-end gap-3 w-full">
                {(selectedCategory !== 'semua' || selectedYear !== 'semua' || searchQuery) ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('semua');
                      setSelectedYear('semua');
                      setCurrentPage(1);
                    }}
                    className="flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-bold text-rose-600 hover:text-rose-700 bg-rose-50 border border-rose-100 hover:bg-rose-100 rounded-xl transition cursor-pointer w-full sm:w-auto"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Reset Filter</span>
                  </button>
                ) : (
                  <span className="text-[10px] text-slate-400 font-medium italic hidden sm:inline">
                    Gunakan filter di atas untuk saring buku...
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Book Display */}
      {filteredBooks.length > 0 ? (
        <div className="space-y-6">
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-6">
              {paginatedBooks.map((b: Book, idx: number) => (
                <div
                  key={b.id}
                  onClick={() => handleBookClick(b)}
                  className="bg-white rounded-2xl p-5 md:p-6 lg:p-7 border border-slate-200/90 shadow-2xs hover:shadow-lg hover:shadow-indigo-100/30 hover:border-indigo-300 transition-all duration-300 cursor-pointer group flex flex-col md:flex-row gap-6 md:items-start"
                >
                  <div className="flex shrink-0 justify-center md:block">
                    <LazyBookCover
                      coverUrl={b.coverUrl}
                      title={b.title}
                      className="w-24 h-34 md:w-32 md:h-44 lg:w-36 lg:h-50 rounded-xl shadow-md border border-slate-100/50"
                      targetWidth={300}
                      hoverEffect
                      priority={idx < 4}
                    />
                  </div>

                  <div className="flex-1 min-w-0 space-y-4">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100">
                          <ShieldCheck className="w-3.5 h-3.5" />
                          <span className="text-[11px] font-mono font-bold tracking-tight">
                            {b.uibn}
                          </span>
                        </div>
                        <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wide">
                          Edisi {b.edition || '1'}
                        </span>
                        <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-500 text-[10px] font-bold">
                          {b.category}
                        </span>
                      </div>

                      <h3 className="font-extrabold text-slate-900 text-base md:text-xl lg:text-2xl leading-tight group-hover:text-indigo-600 transition">
                        {b.title}
                      </h3>
                      {b.subtitle && (
                        <p className="text-xs md:text-sm text-slate-500 italic line-clamp-1 font-medium">
                          {b.subtitle}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-xs md:text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2.5 text-slate-500">
                          <UserCheck className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="truncate">Penulis: <strong className="text-slate-900">{b.authorName}</strong></span>
                        </div>
                        <div className="flex items-center gap-2.5 text-slate-500">
                          <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                          <span className="truncate">Penerbit: <strong className="text-slate-800">{b.publisherName}</strong></span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2.5 text-slate-500">
                          <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                          <span>Tahun Terbit: <strong className="text-slate-800">{b.publicationYear}</strong></span>
                        </div>
                        <div className="flex items-center gap-2.5 text-slate-500">
                          <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                          <span>Bahasa: <strong className="text-slate-800">{b.language || 'Indonesia'}</strong></span>
                        </div>
                      </div>
                    </div>

                    {b.synopsis && (
                      <p className="text-xs md:text-sm text-slate-500 line-clamp-2 leading-relaxed bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                        {b.synopsis.replace(/<[^>]*>/g, '')}
                      </p>
                    )}

                    <div className="mt-4 pt-3.5 border-t border-slate-100/80 space-y-3">
                      <div className="flex items-center justify-between text-[10px] md:text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3.5 h-3.5" />
                          {b.pages || 0} Hal
                        </span>
                        <span className="flex items-center gap-1">
                          <Layers className="w-3.5 h-3.5" />
                          {b.editionType || 'Orisinal'}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-2 pt-1">
                        <div>
                          {b.purchaseUrl && (
                            <a
                              href={b.purchaseUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition shadow-md shadow-emerald-200 text-xs md:text-sm font-black tracking-wide shrink-0"
                            >
                              <ShoppingCart className="w-4 h-4" />
                              <span>Beli Buku</span>
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-indigo-600 font-bold text-xs shrink-0 cursor-pointer hover:underline">
                          <span>Detail</span>
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {paginatedBooks.map((b: Book, idx: number) => (
                <div
                  key={b.id}
                  onClick={() => handleBookClick(b)}
                  className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-indigo-300 transition cursor-pointer group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                >
                  <div className="flex gap-4 items-center min-w-0">
                    <LazyBookCover
                      coverUrl={b.coverUrl}
                      title={b.title}
                      className="w-12 h-16 rounded-lg"
                      targetWidth={200}
                      priority={idx < 6}
                    />

                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-mono font-bold bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {b.uibn}
                        </span>
                        <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600">
                          {b.category}
                        </span>
                      </div>

                      <h3 className="font-bold text-slate-900 text-sm group-hover:text-indigo-600 transition truncate max-w-md">
                        {b.title}
                      </h3>

                      <p className="text-xs text-slate-500">
                        Penulis: <span className="font-semibold text-slate-700">{b.authorName}</span> • Diterbitkan Tahun {b.publicationYear}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0 text-xs font-bold text-indigo-600 self-stretch sm:self-center w-full sm:w-auto pt-3.5 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                    <div>
                      {b.purchaseUrl && (
                        <a
                          href={b.purchaseUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white transition inline-flex items-center gap-2 shadow-md shadow-emerald-200 text-xs md:text-sm font-black tracking-wide"
                          title="Beli Buku Ini"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          <span>Beli Buku</span>
                        </a>
                      )}
                    </div>
                    <span className="px-3.5 py-2 rounded-xl bg-indigo-50 group-hover:bg-indigo-600 group-hover:text-white transition flex items-center gap-1.5 shrink-0">
                      <Eye className="w-3.5 h-3.5" />
                      <span>Detail</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <PaginationNav
            currentPage={currentPage}
            totalPages={totalBookPages}
            totalItems={filteredBooks.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(size) => setItemsPerPage(size)}
            itemsPerPageOptions={[6, 9, 12, 24, 48]}
            itemLabel="Buku"
          />
        </div>
      ) : (
        <div className="py-12 text-center bg-white border border-slate-200/80 rounded-2xl p-6 space-y-2">
          <p className="text-sm font-bold text-slate-800">Tidak Ada Buku Ditemukan</p>
          <p className="text-xs text-slate-500">
            Coba ubah kata kunci pencarian atau pilih kategori lain.
          </p>
        </div>
      )}

      {/* Share Showcase Modal */}
      <ShareShowcaseModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={profile.name}
        shareUrl={fullShareUrl}
        qrUrl={fullShareUrl}
        type={type}
        publisherCode={profile.publisherCode || undefined}
        totalBooks={stats?.totalBooks || books.length}
        avatarUrl={profile.profileImageUrl || profile.avatarUrl || undefined}
      />
    </div>
  );
};
